import React, { Component } from "react";
import { Button, Badge } from "react-bootstrap";
import CeoCirclePoster from "./CeoCirclePoster";
import "./CeoCircle.css";

/**
 * Builds the slide list: the awardees in API order (year desc, name asc), led by a teaser
 * slide for currentYear when nobody holds that year yet.
 */
export const buildSlides = (awardees, currentYear) => {
  const list = Array.isArray(awardees) ? awardees.slice() : [];
  const hasCurrent = list.some((a) => Number(a.year) === Number(currentYear));
  if (!hasCurrent) {
    list.unshift({ teaser: true, year: currentYear });
  }
  return list;
};

/**
 * Poster carousel for the CEO Circle of Excellence tab.
 * Props: awardees, currentYear, isHr, onEdit(awardee), onRemove(awardee), onAdd()
 * One poster is in focus; the others are scaled back and aria-hidden.
 */
class CeoCircleCarousel extends Component {
  constructor(props) {
    super(props);
    this.state = { cur: 0 };
    this.go = this.go.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  componentDidUpdate(prevProps) {
    // The list shrank (a removal): keep the focused index inside the new bounds.
    if (prevProps.awardees !== this.props.awardees) {
      const n = buildSlides(this.props.awardees, this.props.currentYear).length;
      if (this.state.cur > n - 1) {
        this.setState({ cur: Math.max(0, n - 1) });
      }
    }
  }

  go(i) {
    const n = buildSlides(this.props.awardees, this.props.currentYear).length;
    this.setState({ cur: Math.max(0, Math.min(n - 1, i)) });
  }

  onKeyDown(e) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.go(this.state.cur - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      this.go(this.state.cur + 1);
    }
  }

  jumpToYear(year) {
    const slides = buildSlides(this.props.awardees, this.props.currentYear);
    const i = slides.findIndex((a) => Number(a.year) === Number(year));
    if (i > -1) this.go(i);
  }

  render() {
    const { awardees, currentYear, isHr, onEdit, onRemove, onAdd } = this.props;
    const slides = buildSlides(awardees, currentYear);
    const n = slides.length;
    const cur = Math.min(this.state.cur, n - 1);
    const focused = slides[cur];

    const years = [];
    slides.forEach((a) => {
      if (years.indexOf(a.year) < 0) years.push(a.year);
    });

    return (
      <div className="ceo-circle">
        <div className="ceo-head">
          <div>
            <h3>
              <i className="fa fa-trophy txt-green" aria-hidden="true" /> CEO Circle of Excellence
            </h3>
            <p>Celebrating the Eastvantage people who set the standard, year after year.</p>
          </div>
          <span className="spacer" />
          {isHr && (
            <Button type="button" size="sm" className="btn-primary-2" onClick={() => onAdd && onAdd()}>
              <i className="fa fa-plus" aria-hidden="true" /> Add Awardee
            </Button>
          )}
        </div>

        <div
          className="cr"
          role="region"
          aria-roledescription="carousel"
          aria-label="CEO Circle of Excellence honourees"
          tabIndex={0}
          onKeyDown={this.onKeyDown}
        >
          <button
            type="button"
            className="cr-nav prev"
            aria-label="Previous honouree"
            disabled={cur === 0}
            onClick={() => this.go(cur - 1)}
          >
            <i className="fa fa-chevron-left" aria-hidden="true" />
          </button>
          <div className="cr-stage">
            {slides.map((a, i) => {
              const off = i - cur;
              const abs = Math.abs(off);
              const style = {
                transform: "translateX(-50%) translateX(" + off * 62 + "%) scale(" + (abs === 0 ? 1 : 0.78) + ")",
                opacity: abs === 0 ? 1 : abs === 1 ? 0.5 : 0,
                zIndex: 10 - abs,
                pointerEvents: abs > 1 ? "none" : "auto",
              };
              return (
                <div
                  key={a.teaser ? "teaser" : a.id}
                  className="cr-slide"
                  style={style}
                  aria-hidden={abs !== 0 ? "true" : "false"}
                  onClick={() => abs !== 0 && this.go(i)}
                >
                  <CeoCirclePoster awardee={a} teaser={!!a.teaser} />
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className="cr-nav next"
            aria-label="Next honouree"
            disabled={cur >= n - 1}
            onClick={() => this.go(cur + 1)}
          >
            <i className="fa fa-chevron-right" aria-hidden="true" />
          </button>
        </div>

        <div className="cr-meta">
          <span aria-live="polite">
            <Badge className="tag-badge">{focused.year}</Badge> &nbsp;{cur + 1} of {n}
          </span>
          {isHr && !focused.teaser && (
            <span className="hr-actions">
              <Button type="button" variant="outline-secondary" size="sm" onClick={() => onEdit && onEdit(focused)}>
                <i className="fa fa-pencil" aria-hidden="true" /> Edit
              </Button>
              <Button type="button" variant="outline-danger" size="sm" onClick={() => onRemove && onRemove(focused)}>
                <i className="fa fa-trash" aria-hidden="true" /> Remove
              </Button>
            </span>
          )}
        </div>

        <div className="cr-years" role="group" aria-label="Jump to year">
          {years.map((y) => {
            const on = Number(y) === Number(focused.year);
            return (
              <Button
                key={y}
                type="button"
                size="sm"
                variant={on ? undefined : "outline-secondary"}
                className={on ? "btn-primary-2" : ""}
                aria-pressed={on ? "true" : "false"}
                onClick={() => this.jumpToYear(y)}
              >
                {y}
              </Button>
            );
          })}
        </div>
      </div>
    );
  }
}

export default CeoCircleCarousel;
